import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, Merge as MergeIcon } from 'lucide-react';
import { toast } from 'sonner';
import CustomerTabsBar from '../../components/CustomerTabsBar';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMergeCustomers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [primaryId, setPrimaryId] = useState(null);
  const [secondaryIds, setSecondaryIds] = useState(new Set());
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    axios.get(`${API}/customers`, { withCredentials: true })
      .then(({ data }) => setCustomers(data.filter(c => !c.merged_into)))
      .catch(console.error);
  }, []);

  const filtered = search ? customers.filter(c => `${c.name} ${c.email} ${c.phone} ${c.address}`.toLowerCase().includes(search.toLowerCase())) : customers;

  const toggleSecondary = (id) => {
    const next = new Set(secondaryIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSecondaryIds(next);
  };

  const doMerge = async () => {
    if (!primaryId || secondaryIds.size === 0) {
      toast.error('Pick a primary and at least one duplicate');
      return;
    }
    if (!window.confirm(`Merge ${secondaryIds.size} duplicate(s) into the selected primary? This cannot be undone.`)) return;
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/admin/customers/merge`, {
        primary_id: primaryId,
        secondary_ids: Array.from(secondaryIds),
      }, { withCredentials: true });
      const m = data.merged_count || {};
      toast.success(`Merged: ${m.invoices||0} invoices, ${m.estimates||0} estimates, ${m.appointments||0} jobs, ${m.memberships||0} memberships, ${m.timeline||0} timeline events`);
      setSecondaryIds(new Set());
      setPrimaryId(null);
      // Refresh list
      const { data: list } = await axios.get(`${API}/customers`, { withCredentials: true });
      setCustomers(list.filter(c => !c.merged_into));
    } catch (err) { toast.error(err.response?.data?.detail || 'Merge failed'); }
    setMerging(false);
  };

  return (
    <div data-testid="admin-merge-page" className="space-y-4">
      <CustomerTabsBar userRole={user?.role} />
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2"><MergeIcon className="w-6 h-6 text-[#D4AF37]" /> Merge Duplicates</h2>
        <p className="text-[#A3A3A3]">Pick one customer to keep, mark the rest as duplicates → all data flows to the primary</p>
      </div>

      <Input data-testid="merge-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search to narrow list" className="max-w-md bg-[#0A0A0A] border-[#262626] text-white" />

      <div className="grid gap-2 max-h-[600px] overflow-y-auto">
        {filtered.map(c => {
          const isPrimary = primaryId === c.id;
          const isSecondary = secondaryIds.has(c.id);
          return (
            <div key={c.id} className={`bg-[#141414] border rounded-md p-3 flex items-center gap-3 ${isPrimary ? 'border-[#D4AF37]' : isSecondary ? 'border-red-700' : 'border-[#262626]'}`}>
              <Users className="w-4 h-4 text-[#D4AF37]" />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{c.name}</p>
                <p className="text-[#A3A3A3] text-xs truncate">{c.email} {c.phone && `• ${c.phone}`} {c.address && `• ${c.address}`}</p>
              </div>
              <Button data-testid={`merge-primary-${c.id}`} size="sm" onClick={() => { setPrimaryId(c.id); if (isSecondary) toggleSecondary(c.id); }} variant={isPrimary ? 'default' : 'outline'} className={isPrimary ? 'bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]' : 'border-[#262626] text-white hover:bg-[#262626]'}>
                {isPrimary ? '✓ Primary' : 'Make Primary'}
              </Button>
              <Button data-testid={`merge-dup-${c.id}`} size="sm" onClick={() => { if (isPrimary) return; toggleSecondary(c.id); }} disabled={isPrimary} variant={isSecondary ? 'default' : 'outline'} className={isSecondary ? 'bg-red-700 text-white hover:bg-red-800' : 'border-[#262626] text-white hover:bg-[#262626]'}>
                {isSecondary ? '✓ Duplicate' : 'Mark Duplicate'}
              </Button>
            </div>
          );
        })}
      </div>

      <Button data-testid="merge-submit" onClick={doMerge} disabled={merging || !primaryId || secondaryIds.size === 0} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]">
        Merge {secondaryIds.size} into Primary
      </Button>
    </div>
  );
};

export default AdminMergeCustomers;
